/* PWA Install Banner — Casa Orterie
 *
 * Mostra un banner discreto, coerente col brand, per invitare l'utente a
 * installare la Web App sul proprio dispositivo.
 *
 * Comportamenti:
 *  - Android/Desktop Chrome/Edge: intercetta `beforeinstallprompt`, previene
 *    il mini-infobar di default e mostra un pulsante custom "Installa l'app".
 *    Al click chiama `prompt()` nativo. Se l'utente dismette, salviamo il
 *    timestamp in localStorage e nascondiamo il banner per 14 giorni.
 *  - iOS Safari: non supporta `beforeinstallprompt`. Mostriamo un breve
 *    hint "Per installare: tocca Condividi → Aggiungi a schermata Home"
 *    con l'iconcina del tasto Condividi.
 *  - Nascosto in modalità standalone (app già installata).
 */
import { useCallback, useEffect, useState } from "react";
import { Download, Share, Plus, X } from "lucide-react";

const DISMISS_KEY = "co_pwa_install_dismissed_v1";
const DISMISS_DAYS = 14;

function isDismissedRecently() {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const ts = parseInt(raw, 10);
    if (!ts || Number.isNaN(ts)) return false;
    const ageDays = (Date.now() - ts) / (1000 * 60 * 60 * 24);
    return ageDays < DISMISS_DAYS;
  } catch {
    return false;
  }
}

function isIOS() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || navigator.vendor || "";
  // iPhone / iPad / iPod. Esclude Chrome iOS (CriOS) e Firefox iOS (FxiOS)
  // perché su quelli non è possibile aggiungere a Home comunque — ma su
  // Safari sì.
  const isiOSDevice = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|OPiOS|EdgiOS/.test(ua);
  return isiOSDevice && isSafari;
}

function isStandalone() {
  if (typeof window === "undefined") return false;
  // iOS Safari expone navigator.standalone; Chrome/Android usa display-mode.
  const displayStandalone = window.matchMedia?.("(display-mode: standalone)").matches;
  const iosStandalone = window.navigator.standalone === true;
  return !!(displayStandalone || iosStandalone);
}

export default function PWAInstallBanner() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [variant, setVariant] = useState(null); // "android" | "ios"

  useEffect(() => {
    if (isStandalone()) return;
    if (isDismissedRecently()) return;

    // Android/Desktop: aspetta beforeinstallprompt
    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setVariant("android");
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    const onInstalled = () => {
      setVisible(false);
      setDeferredPrompt(null);
    };
    window.addEventListener("appinstalled", onInstalled);

    // iOS: nessun evento, mostriamo l'hint dopo un breve delay (meno
    // invasivo di un popup immediato).
    let iosTimer;
    if (isIOS()) {
      iosTimer = setTimeout(() => {
        if (!isStandalone() && !isDismissedRecently()) {
          setVariant("ios");
          setVisible(true);
        }
      }, 2500);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
      if (iosTimer) clearTimeout(iosTimer);
    };
  }, []);

  const dismiss = useCallback(() => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      /* ignore */
    }
    setVisible(false);
  }, []);

  const install = useCallback(async () => {
    if (!deferredPrompt) return;
    try {
      await deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      if (choice?.outcome === "dismissed") {
        dismiss();
      } else {
        setVisible(false);
      }
    } catch {
      dismiss();
    } finally {
      setDeferredPrompt(null);
    }
  }, [deferredPrompt, dismiss]);

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-label="Installa l'app di Casa Orterie"
      data-testid="pwa-install-banner"
      className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:bottom-6 sm:max-w-sm z-[1000] rounded-2xl border border-[#E8DFCF] bg-[#FBF7EE] shadow-[0_12px_40px_-10px_rgba(60,27,36,0.35)] backdrop-blur-sm"
    >
      <div className="flex items-start gap-3 p-4">
        <div className="shrink-0 mt-0.5 w-10 h-10 rounded-full bg-[#5C0F1F] text-[#F5EEE0] flex items-center justify-center">
          {variant === "ios" ? (
            <Share className="w-5 h-5" />
          ) : (
            <Download className="w-5 h-5" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-[0.95rem] font-serif text-[#3A1B24] leading-tight">
            {variant === "ios" ? "Aggiungi alla Home" : "Installa l'app"}
          </h3>
          {variant === "ios" ? (
            <p
              data-testid="pwa-ios-instructions"
              className="mt-1 text-xs text-[#3A1B24]/70 leading-relaxed"
            >
              Per averla sempre a portata di mano: tocca{" "}
              <Share className="inline w-3.5 h-3.5 -mt-0.5 mx-0.5" aria-label="Condividi" />
              Condividi, poi{" "}
              <span className="whitespace-nowrap">
                <Plus className="inline w-3.5 h-3.5 -mt-0.5 mx-0.5" aria-label="Più" />
                Aggiungi a schermata Home
              </span>
              .
            </p>
          ) : (
            <p className="mt-1 text-xs text-[#3A1B24]/70 leading-relaxed">
              Aggiungi Casa Orterie al tuo dispositivo per consultare la carta
              anche offline.
            </p>
          )}
          {variant === "android" && (
            <button
              type="button"
              data-testid="pwa-install-button"
              onClick={install}
              className="mt-3 inline-flex items-center justify-center rounded-full bg-[#5C0F1F] text-[#F5EEE0] px-4 py-1.5 text-sm transition hover:bg-[#731426] focus:outline-none focus:ring-2 focus:ring-[#5C0F1F]/40"
            >
              Installa
            </button>
          )}
        </div>
        <button
          type="button"
          aria-label="Chiudi"
          data-testid="pwa-install-dismiss"
          onClick={dismiss}
          className="shrink-0 -mt-1 -mr-1 p-1.5 text-[#3A1B24]/50 hover:text-[#3A1B24] transition"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
